import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { subscriptionsAPI } from '../../api/subscriptionsAPI';

// Async thunks
export const fetchSubscriptionPlans = createAsyncThunk(
  'subscriptions/fetchPlans',
  async (_, { rejectWithValue }) => {
    try {
      const data = await subscriptionsAPI.getSubscriptionPlans();
      return data;
    } catch (error) {
      // Provide more detailed error messages
      let errorMessage = 'Failed to fetch subscription plans';
      
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.error || 
                      error.response.data?.detail || 
                      `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Network error: Unable to reach server. Please check your connection.';
      } else {
        // Something else happened
        errorMessage = error.message || errorMessage;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchMySubscription = createAsyncThunk(
  'subscriptions/fetchMySubscription',
  async (userId, { rejectWithValue }) => {
    try {
      const data = await subscriptionsAPI.getMySubscription(userId);
      return data;
    } catch (error) {
      // Don't treat 404 as an error - user might not have a subscription yet
      if (error.response?.status === 404) {
        return { subscription: null };
      }
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch subscription');
    }
  }
);

export const checkoutSubscription = createAsyncThunk(
  'subscriptions/checkout',
  async ({ userId, planId, billingData, paymobToken }, { rejectWithValue }) => {
    try {
      const data = await subscriptionsAPI.checkoutSubscription(userId, planId, billingData, paymobToken);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to checkout subscription');
    }
  }
);

export const activateSubscription = createAsyncThunk(
  'subscriptions/activate',
  async ({ subscriptionId, transactionId, payload }, { rejectWithValue }) => {
    try {
      const data = await subscriptionsAPI.activateSubscription(subscriptionId, transactionId, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to activate subscription');
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscriptions/cancel',
  async (userId, { rejectWithValue }) => {
    try {
      const data = await subscriptionsAPI.cancelSubscription(userId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to cancel subscription');
    }
  }
);

export const fetchEntitlements = createAsyncThunk(
  'subscriptions/fetchEntitlements',
  async (userId, { rejectWithValue }) => {
    try {
      const data = await subscriptionsAPI.getEntitlements(userId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch entitlements');
    }
  }
);

const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState: {
    plans: [],
    mySubscription: null,
    entitlements: {},
    checkoutUrl: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCheckoutUrl: (state) => {
      state.checkoutUrl = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch plans
    builder
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch my subscription
    builder
      .addCase(fetchMySubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMySubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.mySubscription = action.payload.subscription;
      })
      .addCase(fetchMySubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Checkout
    builder
      .addCase(checkoutSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkoutSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.mySubscription = action.payload.subscription;
        state.checkoutUrl = action.payload.checkout_url;
      })
      .addCase(checkoutSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Activate
    builder
      .addCase(activateSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.mySubscription = action.payload.subscription;
      })
      .addCase(activateSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Cancel
    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.mySubscription = action.payload.subscription;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch entitlements
    builder
      .addCase(fetchEntitlements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEntitlements.fulfilled, (state, action) => {
        state.loading = false;
        state.entitlements = action.payload.entitlements || {};
      })
      .addCase(fetchEntitlements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCheckoutUrl } = subscriptionsSlice.actions;
export default subscriptionsSlice.reducer;

